import { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useAuthStore from './store/useAuthStore.js';
import useUIStore from './store/useUIStore.js';
import { useSSE } from './hooks/useSSE.js';
import { syncPushSubscription } from './lib/pushNotifications.js';

// Layouts & Config
import Layout from './components/Layout.jsx';
import ToastContainer from './components/ToastContainer.jsx';
import { publicRoutes, protectedRoutes, adminRoutes, standaloneRoutes } from './routes/routes.config.jsx';

/**
 * Loading Fallback Component
 */
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      <p className="text-sm font-medium text-muted-foreground">Loading...</p>
    </div>
  </div>
);

/**
 * Protected Route Wrapper
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
}

/**
 * Public Route Wrapper
 */
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return children;
}

/**
 * Admin Route Wrapper
 */
function AdminRoute({ children }) {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.globalRole !== 'ADMIN') return <Navigate to="/" replace />;

  return children;
}

function SessionServices() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  useSSE();
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void syncPushSubscription({ requestPermission: false });
  }, [isAuthenticated]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const isLoading = useAuthStore((state) => state.isLoading);
  const initialize = useAuthStore((state) => state.initialize);
  const standalone = location.pathname.startsWith('/p/') || /^\/prototypes\/[^/]+\/demo\/?$/.test(location.pathname);

  useEffect(() => {
    if (!location.pathname.startsWith('/p/') && isLoading) void initialize();
  }, [location.pathname, isLoading, initialize]);

  return (
    <Suspense fallback={<PageLoader />}>
      {!standalone && <SessionServices />}
      <Routes>
        {standaloneRoutes.map(({ path, element, authenticated }) => (
          <Route key={path} path={path} element={authenticated ? <ProtectedRoute>{element}</ProtectedRoute> : element} />
        ))}
        {/* Protected Area (with Layout) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Redirect root to dashboard */}
          <Route index element={<Navigate to="/dashboard" replace />} />

          {protectedRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}

          {/* Admin Only Routes */}
          {adminRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={<AdminRoute>{element}</AdminRoute>} />
          ))}
        </Route>

        {/* Public Auth Area */}
        {publicRoutes.map(({ path, element }) => (
          <Route key={path} path={path} element={<PublicRoute>{element}</PublicRoute>} />
        ))}

        {/* Fallback for undefined routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!standalone && <ToastContainer />}
    </Suspense>
  );
}

function App() {
  const initTheme = useUIStore((state) => state.initTheme);

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  return (
    <BrowserRouter basename={'/'}>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
